import { Button, Popconfirm, Space, Table, Select, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { deleteLeavePolicy, loadAllLeavePolicy } from '../../redux/rtk/features/leavePolicy/leavePolicySlice';
import { getAllMaster } from "../../redux/rtk/features/masters/masterSlice";
import { CompanyDetailsGetbyId } from '../../redux/rtk/features/settingSlice/settingSlice';
import { isCompOffLeaveType } from '../compOffPolicy/utils/compOffLeaveType';
import LeavePolicyComboViewModal from './LeavePolicyComboViewModal';

const { Option } = Select;

const LeavePolicyTable = ({ onEdit, onYearChange }) => {
  const { list, loading } = useSelector((state) => state.leavePolicy);
  const masterData = useSelector((state) => state.masters.masters);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [comboView, setComboView] = useState({
    open: false,
    leavePolicyId: null,
    policyTitle: "",
  });
  const dispatch = useDispatch();
  const onYearChangeRef = useRef(onYearChange);
  onYearChangeRef.current = onYearChange;

 const companyDetail = useSelector((state) => state?.setting?.list) || [];
  useEffect(() => {
    dispatch(getAllMaster());
    dispatch(CompanyDetailsGetbyId());
  }, [dispatch]);

 useEffect(() => {
     if (selectedYear != null) return;
     if (!masterData) return;

     const useCalendarYear = Boolean(companyDetail?.IsLeaveCalenderYear);
     const years = useCalendarYear
       ? masterData?.CalendarYearDetails
       : masterData?.FinancialYearDetails;

     if (!years?.length) return;

     if (useCalendarYear) {
       setFinancialYears(masterData.FinancialYearDetails || []);
     } else {
       setFinancialYears(masterData.FinancialYearDetails);
     }

     const defaultYear = years.find((year) => year.IsCurrent)?.YearId;

     if (defaultYear) {
       setSelectedYear(defaultYear);
       onYearChangeRef.current?.(defaultYear);
     }
  }, [masterData, companyDetail, selectedYear]);
  // Load policies when selected year changes
  useEffect(() => {
    if (selectedYear) {
      dispatch(loadAllLeavePolicy(selectedYear));
    }
  }, [selectedYear, dispatch]);

  // Function to find the current financial year based on current date
  const findCurrentFinancialYear = (years) => {
    if (!years || years.length === 0) return null;
    
    const currentDate = new Date();
    
    // Find the financial year where current date falls between StartDate and EndDate
    return years.find(year => {
      const startDate = new Date(year.StartDate);
      const endDate = new Date(year.EndDate);
      return currentDate >= startDate && currentDate <= endDate;
    });
  };

  const handleYearChange = (yearId) => {
    setSelectedYear(yearId);
    onYearChange?.(yearId);
  };

  const handleDelete = (id) => {
    dispatch(deleteLeavePolicy(id))
      .then(() => {
        // Pass the selected year to reload policies for the same year after deletion
        dispatch(loadAllLeavePolicy(selectedYear));
      })
      .catch((error) => {
        // console.error("Error deleting leave policy:", error);
      });
  };

  const addKeys = (arr) => arr.map((i, index) => ({ ...i, key: i.LeavePolicyId || i.id, serial: index + 1 }));

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'serial',
      key: 'serial',
      width: 80,
    },
    {
      title: 'Leave Type',
      dataIndex: 'LeaveTypeName',
      key: 'LeaveTypeName',
      width: 220,
      // render: (leaveType) => leaveType ? leaveType.leaveType || leaveType.LeaveTypeName : 'N/A',
    },
    {
      title: 'Policy Name',
      dataIndex: 'LeavePolicyGroupName',
      key: 'LeavePolicyGroupName',
      width: 220,
      render: (name, record) => name || record.name || 'N/A',
    },
    {
      title: 'Limit (Year)',
      dataIndex: 'YearlyLimit',
      key: 'YearlyLimit',
      width: 120,
      render: (YearlyLimit) => YearlyLimit || '0',
    },
    {
      title: 'Limit (Month)',
      dataIndex: 'MonthlyLimit',
      key: 'MonthlyLimit',
      width: 130,
      render: (MonthlyLimit) => MonthlyLimit || '0',
    },
    {
      title: 'Paid Leave',
      dataIndex: 'paid',
      key: 'paid',
      width: 120,
      render: (paid, record) => (paid || record.IsPaidLeave ? 'Yes' : 'No'),
    },
    {
      title: 'Carry Forward',
      dataIndex: 'carryForward',
      key: 'carryForward',
      width: 140,
      render: (carryForward, record) => (carryForward || record.IsCarryForward ? 'Yes' : 'No'),
    },
    {
      title: 'Encashable',
      dataIndex: 'encashable',
      key: 'encashable',
      width: 120,
      render: (encashable, record) => (encashable || record.IsEnCashable ? 'Yes' : 'No'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space>
          {isCompOffLeaveType(record.LeaveTypeName) && (
            <Tooltip title="View Combo-Off">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() =>
                  setComboView({
                    open: true,
                    leavePolicyId: record.LeavePolicyId || record.id,
                    policyTitle:
                      record.LeavePolicyGroupName || record.LeaveTypeName || "",
                  })
                }
              />
            </Tooltip>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this policy?"
            onConfirm={() => handleDelete(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex my-2 justify-between">
        <div className="w-50">
          <h4 className="text-xl mb-2">Leave Policies</h4>
        </div>
        <div className="flex items-center">
          <Select
            style={{ width: 180, marginRight: 10 }}
            placeholder={
              companyDetail?.IsLeaveCalenderYear
                ? "Select Year"
                : "Select Financial Year"
            }
            value={selectedYear}
            onChange={handleYearChange}
          >
            {companyDetail?.IsLeaveCalenderYear ? (
              masterData?.CalendarYearDetails.map((year) => (
                <Option key={year.YearId} value={year.YearId}>
                  {year.YearName}
                </Option>
              ))
            ) : (
              financialYears.map((year) => (
                <Option key={year.YearId} value={year.YearId}>
                  {year.YearName}
                </Option>
              ))
            )}
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="ant-spin ant-spin-lg ant-spin-spinning">
            <span className="ant-spin-dot ant-spin-dot-spin">
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
            </span>
          </div>
          <p className="mt-4 text-gray-600">Loading policies...</p>
        </div>
      ) : !list || list.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">No policies found.</p>
        </div>
      ) : (
        <Table
          scroll={{ x: "max-content", y: "100%" }}
          tableLayout="auto"
          pagination={{
            defaultPageSize: 20,
          }}
          columns={columns}
          dataSource={list ? addKeys(list) : []}
          className="leave-policy-table"
        />
      )}

      <LeavePolicyComboViewModal
        open={comboView.open}
        leavePolicyId={comboView.leavePolicyId}
        policyTitle={comboView.policyTitle}
        onClose={() =>
          setComboView({ open: false, leavePolicyId: null, policyTitle: "" })
        }
      />
    </>
  );
};

export default LeavePolicyTable;